import { useState } from 'react';
import { Alert, Button, IconButton, TextField } from '@cere/cere-design-system';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import type { SettingDecl } from '@/lib/manifest/types';

interface Props {
  vaultId: string;
  settingDecls: SettingDecl[];
  settings: Record<string, string>;
  onChange: (key: string, value: string) => void;
  onSubmit: () => void;
}

export function ConnectAgentForm({ vaultId, settingDecls, settings, onChange, onSubmit }: Props) {
  const [revealed, setRevealed] = useState<Record<string, boolean>>({});

  return (
    <div className="connect-form">
      <Alert className="connect-notice connect-mono" severity="success" variant="standard">
        vault ready — {vaultId}
      </Alert>

      {settingDecls.length > 0 && (
        <div className="connect-fields">
          <div className="connect-fields__eyebrow">Connection settings</div>
          {settingDecls.map((s) => {
            const label = s.label || s.key;
            const isSecret = s.type === 'secret';
            const show = revealed[s.key] ?? false;
            const value = settings[s.key] ?? (s.default !== undefined ? String(s.default) : '');
            const fieldId = `connect-set-${s.key}`;
            return (
              <div key={s.key} className="connect-field">
                <label className="connect-field__label" htmlFor={fieldId}>
                  {label}
                  {s.required && <span className="connect-field__req" data-testid="required-marker"> *</span>}
                </label>
                <div className="connect-field__control">
                  <TextField
                    id={fieldId}
                    fullWidth
                    type={isSecret && !show ? 'password' : 'text'}
                    value={value}
                    onChange={(e) => onChange(s.key, e.target.value)}
                    placeholder={s.type}
                    {...(s.description ? { helperText: s.description } : {})}
                  />
                  {isSecret && (
                    <IconButton
                      type="button"
                      aria-label={show ? `Hide ${label}` : `Show ${label}`}
                      size="small"
                      className="connect-field__reveal"
                      onClick={() => setRevealed((r) => ({ ...r, [s.key]: !show }))}
                    >
                      {show ? <VisibilityOffIcon sx={{ fontSize: 16 }} /> : <VisibilityIcon sx={{ fontSize: 16 }} />}
                    </IconButton>
                  )}
                </div>
                {isSecret && <div className="connect-field__hint">Stored encrypted in your vault.</div>}
              </div>
            );
          })}
        </div>
      )}

      <Button fullWidth className="connect-action" onClick={onSubmit}>Connect agent</Button>
    </div>
  );
}
